<template>
  <div>
    <div class="form-content">
      <Selector
        ref="Selector"
        :petitionSelector="petitionSelector"
        :donationSelector="donationSelector"
        :petitionHandle="petitionHandle"
        @firstTouch="logFirstTouch('leaf relief type')"
      ></Selector>

      <!-- Form -->
      <div>
        <!-- First Name -->
        <div class="wwf-field">
          <input
            name="firstName"
            type="text"
            v-model="supporter.firstName"
            v-validate="'required'"
            data-vv-as="first name"
            :class="{
              error: errors.has('firstName'),
              empty: !supporter.firstName,
            }"
            placeholder=" "
            autocomplete="given-name"
            v-on:focus="logFirstTouch('first name')"
          />
          <label for="firstName">First Name*</label>
          <span class="wwf-alert">
            <p v-if="errors.has('firstName')">
              {{ errors.first("firstName") }}
            </p>
          </span>
        </div>

        <!-- Last Name -->
        <div class="wwf-field">
          <input
            name="lastName"
            type="text"
            v-model="supporter.lastName"
            v-validate="'required'"
            data-vv-as="last name"
            :class="{
              error: errors.has('lastName'),
              empty: !supporter.lastName,
            }"
            placeholder=" "
            autocomplete="family-name"
            v-on:focus="logFirstTouch('last name')"
          />
          <label for="lastName">Last Name*</label>
          <span class="wwf-alert">
            <p v-if="errors.has('lastName')">
              {{ errors.first("lastName") }}
            </p>
          </span>
        </div>

        <!-- Email -->
        <div class="wwf-field">
          <input
            id="email"
            name="email"
            type="email"
            v-model="supporter.email"
            v-validate="{ required: true, leafReliefCheckEmail: true }"
            :class="{
              error: errors.has('email'),
              empty: !supporter.email,
            }"
            placeholder=" "
            autocomplete="email"
            v-on:focus="logFirstTouch('email')"
          />
          <label for="email">Email*</label>
          <span class="wwf-alert">
            <p v-if="errors.has('email')">
              {{ errors.first("email") }}
            </p>
          </span>
        </div>

        <!-- Postcode -->
        <div class="wwf-field">
          <input
            name="postcode"
            type="text"
            v-model="supporter.postcode"
            v-validate="{
              required: true,
              alpha_num: true,
              postcodeIsValid: true,
            }"
            data-vv-as="postcode"
            :class="{
              error: errors.has('postcode'),
              empty: !supporter.postcode,
            }"
            placeholder=" "
            @input="updateElectorates"
            autocomplete="postal-code"
            v-on:focus="logFirstTouch('postcode')"
          />
          <label for="postcode">Postcode*</label>
          <span class="wwf-alert">
            <p v-if="errors.has('postcode')">
              {{ errors.first("postcode") }}
            </p>
          </span>
        </div>

        <!-- Phone Number -->
        <div class="wwf-field">
          <input
            name="phone"
            type="tel"
            v-model="supporter.phoneNumber"
            min="8"
            max="12"
            v-validate="{ required: true, phoneNumberIsValid: true }"
            data-vv-as="phone"
            :class="{
              error: errors.has('phone'),
              empty: !supporter.phoneNumber,
            }"
            placeholder=" "
            autocomplete="tel"
            v-on:focus="logFirstTouch('phone')"
          />
          <label for="phone">Phone*</label>
          <span class="wwf-alert">
            <p v-if="errors.has('phone')">
              {{ errors.first("phone") }}
            </p>
          </span>

          <!-- Electorates -->
          <div class="wwf-field" v-if="isSpeciesForm && isValidPostcode">
            <select required v-model="selectedElectorate">
              <option
                v-for="electorate in electoratesList"
                :key="electorate"
                :value="electorate"
              >
                {{ electorate }}
              </option>
            </select>
            <label>Electorate</label>
          </div>
        </div>

        <!-- Display Name -->
        <label class="checkbox">
          Yes, display my name
          <input
            type="checkbox"
            name="displayName"
            checked
            v-model="supporter.agreesDisplayName"
            v-on:focus="logFirstTouch('display my name')"
          />
          <span class="checkmark"></span>
        </label>
      </div>
      <!-- End of form -->

      <div class="submit-email-error" v-if="isEmailInError">
        <p>{{ duplicateEmailMessage }}</p>
      </div>

      <!-- Next button -->
      <button
        type="button"
        class="plain"
        name="stepOneNext"
        v-on:click="next()"
        href="#form-top"
      >
        <span v-if="!isSaving">
          {{ isDonation ? donationSelector : petitionSelector }}
        </span>
        <span v-if="isSaving">
          <i class="far fa-spinner-third fa-spin"></i>
        </span>
      </button>

      <!-- PRIVACY STATEMENT -->
      <div class="privacy-statement">
        <p>
          By signing you accept
          <a href="/privacy-policy/privacy-policy" target="_blank">
            WWF-Australia&rsquo;s Privacy Policy.
          </a>
        </p>
      </div>

      <!-- View Email -->
      <button
        v-if="isSpeciesForm && readyToViewEmail"
        type="button"
        class="secondary email-btn"
        name="viewMessage"
        @click="toggleMessage"
      >
        View your email to the representative
        <i class="fa fa-angle-up" v-if="viewMessage"></i>
        <i class="fa fa-angle-down" v-if="!viewMessage"></i>
      </button>
    </div>

    <div class="ra-message-content" v-if="viewMessage && readyToViewEmail">
      <FormMessageViewer
        :firstName="supporter.firstName"
        :lastName="supporter.lastName"
        :email="supporter.email"
        :postcode="supporter.postcode"
        :electorateName="selectedElectorate"
        :isValidPostcode="isValidPostcode"
        :messageId="messageId"
        class="message"
      >
      </FormMessageViewer>
      <div class="back-top">
        <div class="clickable" @click="scrollTop">
          <i class="fa fa-angle-up"></i>
          <p>Back to top</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FormMessageViewer from "../threatened-species/form-components/MessageViewer.vue";
import Selector from "./Selector";
import gtm from "../../utils/gtm";

import { mapState, mapGetters } from "vuex";
import { Validator } from "vee-validate";

const { detect } = require("detect-browser");
const browser = detect();

export default {
  name: "LeafReliefDetails",
  components: {
    Selector,
    FormMessageViewer,
  },

  data() {
    return {
      isSaving: false,
      timeout: null,
      lastTimestamp: Date.now(),
      emailRegex: /.+\@.+\..+/,
      checkEmailLoading: false,
      isEmailInError: false,

      isValidPostcode: false,
      electoratesList: [],
      selectedElectorate: "",

      formTouched: false,
      viewMessage: false,
    };
  },
  computed: {
    ...mapGetters(["isDonation", "selection"]),
    ...mapState({
      supporter: (state) => state.leafRelief.details,
      donation: (state) => state.donation.donation,
    }),
    readyToViewEmail() {
      return (
        this.selectedElectorate &&
        this.supporter.firstName &&
        this.supporter.lastName &&
        this.supporter.email
      );
    },
  },
  props: {
    programName: {
      type: String,
    },
    datalayerName: {
      type: String,
    },

    // IVE
    sendToIve: {
      type: Boolean,
    },
    iveSupplier: {
      type: String,
    },
    iveSource: {
      type: String,
    },

    // Selectors
    petitionSelector: {
      type: String,
    },
    donationSelector: {
      type: String,
    },

    petitionHandle: {
      type: String,
    },

    duplicateEmailMessage: {
      type: String,
    },

    messageId: {
      type: String,
    },

    // Change to species form (show electorate etc..)
    isSpeciesForm: {
      type: Boolean,
      default: false,
    },
  },

  created() {
    // If it's a petition, prevent 2 signatures
    // If it's a donation, allow multiple submissions
    const leafReliefCheckEmail = (email) => {
      this.isEmailInError = false;

      if (this.isDonation) {
        return this.checkEmail(email);
      } else {
        // Do not allow multiple submission for the petition
        return new Promise((resolve, reject) => {
          Promise.all([this.checkEmailDuplicate(email), this.checkEmail(email)])
            .then((results) => {
              for (const r of results) {
                if (!r.valid) {
                  this.isEmailInError = true;
                  return resolve(r);
                }
              }
              this.errors.remove("email");
              resolve({ valid: true });
            })
            .catch((err) => {
              console.error(err);
              this.errors.remove("email");
              resolve({ valid: true });
            });
        });
      }
    };

    // Check Phone Number
    let phoneNumberIsValid = (phoneNumber) => {
      if (phoneNumber) {
        const validationRegex = /^(\+?61|0)([0-9]{8,9})$/;
        if (!phoneNumber.replace(/ /g, "").match(validationRegex)) {
          return {
            valid: false,
            data: { message: "Invalid phone number" },
          };
        } else {
          return {
            valid: true,
          };
        }
      } else {
        return {
          valid: true,
        };
      }
    };

    Validator.extend("phoneNumberIsValid", {
      validate: phoneNumberIsValid,
      getMessage: (field, params, data) => data.message,
    });

    Validator.extend("leafReliefCheckEmail", {
      validate: leafReliefCheckEmail,
      getMessage: (field, params, data) => data.message,
    });

    // Check postcode
    Validator.extend("postcodeIsValid", {
      validate: (postcode) => {
        if (this.isValidPostcode) {
          return { valid: true };
        } else {
          return {
            valid: false,
            data: { message: "Invalid postcode" },
          };
        }
      },
      getMessage: (field, params, data) => data.message,
    });
  },
  methods: {
    checkEmailDuplicate(email) {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("checkEmail", {
            campaignId: this.datalayerName,
            email: email,
          })
          .then((result) => {
            if (result) {
              resolve({
                valid: false,
                field: "email",
                data: { message: "Your email has already been submitted" },
              });
            } else {
              resolve({ valid: true });
            }
          });
      });
    },
    checkEmail(email) {
      return new Promise((resolve, reject) => {
        if (email.length > 0 && this.emailRegex.test(email)) {
          let timestamp = Date.now(); // timestamp for this api call
          this.lastTimestamp = timestamp; // 1 , 2 , 3
          this.checkEmailLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validateEmail", email)
            .then((response) => {
              if (response && timestamp >= this.lastTimestamp) {
                this.checkEmailLoading = false;
                this.donation.emailValidation = response;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.checkEmailLoading = false;
              // If it fails, let the user free to continue
              resolve({ valid: true });
            });
        } else {
          resolve({
            valid: false,
            data: { message: "Email invalid" },
          });
        }
      });
    },
    async next() {
      this.logFirstTouch("next");
      try {
        // Exit the function if it's busy
        if (this.isSaving) {
          return;
        }
        this.isSaving = true;

        if (this.isDonation) {
          // Validate the selector as well
          await Promise.all([this.validate(), this.$refs.Selector.validate()]);
          // If it's a donation, save the pending donation and go to the next step for payment
          await this.saveDonation();
          this.$store.dispatch("logLeafProduct", this.supporter.postcode);
        } else {
          // Validate the form
          await this.validate();
          // If it's a petition, save the lead and go to the next step
          await this.savePetition();
          this.$store.dispatch("logLeafProduct", this.supporter.postcode);
          this.$store.commit("resetLeafRelief");
        }

        this.$emit("next");
      } catch (err) {
        console.error(err);
        gtm.formSubmitEvent(this.datalayerName);
      } finally {
        this.isSaving = false;
      }
    },

    validate() {
      return new Promise((resolve, reject) => {
        this.$validator
          .validateAll()
          .then((result) => {
            // Will return true if valid and false if not
            if (result) {
              resolve(result);
            } else {
              console.error("Leaf Relief - Details not valid - result", result);
              reject(result);
            }
          })
          .catch((err) => {
            console.error("Leaf Relief - Details - Error - ", err);
            reject(err);
          });
      });
    },

    async savePetition() {
      try {
        const payload = {
          email: this.supporter.email,
          firstName: this.supporter.firstName,
          lastName: this.supporter.lastName,
          postcode: this.supporter.postcode,
          phone: this.supporter.phoneNumber,
          agreesDisplayName: this.supporter.agreesDisplayName,
          signatureType: this.selection,
          cookieID: this.$cookie.get("_mkto_trk"),
          keepData: true,
          ssoId: this.$store.getters.ssoId,
          programName: this.programName,
          sendToIve: this.sendToIve,
          iveSupplier: this.iveSupplier,
          iveSource: this.iveSource,
        };

        if (browser) {
          payload.browsername = browser.name;
          payload.browserversion = browser.version;
          payload.browseros = browser.os;
        }

        if (this.isSpeciesForm) {
          payload.electorate = this.selectedElectorate;

          // Get electorate details
          const mp = await this.$store.dispatch(
            "getMp",
            this.selectedElectorate
          );
          if (mp) {
            payload.mpName = mp.name;
            payload.mpEmail =
              mp.email && mp.email.length > 0 ? mp.email : mp.manualEmail;

            // Find species count
            const [electorateData, heroSpecies] = await Promise.all([
              this.$store.dispatch("getElectoratesGrade", [
                this.selectedElectorate,
              ]),
              this.$store.dispatch("getHeroSpecies", this.selectedElectorate),
            ]);

            payload.specieCount =
              electorateData[0].numberofthreatenedspeciesinelectorate;
            payload.specieName = heroSpecies.commonName;
          }
        }

        console.log("Save petition payload", payload);

        await this.$store.dispatch("saveLead", {
          campaignId: this.datalayerName,
          lead: payload,
          saveSignature: this.supporter.agreesDisplayName,
        });

        console.log("Leaf Relief Petition submit success");

        // GTM
        gtm.formSubmitSuccessEvent(this.datalayerName);

        // Reset form
        this.isValidPostcode = false;
        this.selectedElectorate = false;
      } catch (err) {
        console.error("Leaf Relief Petition submit error");
        console.error(err);
      }
    },
    async saveDonation() {
      return new Promise((resolve, reject) => {
        this.$refs.Selector.commit();

        // Save pending donation
        // Populate the actual donation store and then save it
        this.donation.firstname = this.supporter.firstName;
        this.donation.lastname = this.supporter.lastName;
        this.donation.email = this.supporter.email;
        this.donation.phone = this.supporter.phoneNumber;
        this.donation.billing.country = null;
        this.donation.billing.state = null;
        this.donation.billing.postcode = this.supporter.postcode;

        this.$store
          .dispatch("saveDonation", {
            agreesDisplayName: this.supporter.agreesDisplayName,
          })
          .then((response) => {
            resolve();
          })
          .catch((error) => {
            console.error("saveDonation error", error);
            reject(error);
          });
      });
    },

    async updateElectorates(event) {
      const newPostcode = event.target.value;
      try {
        if (newPostcode.length === 4) {
          // Get electorates from postcode
          const electorates = (
            await this.$store.dispatch("getElectorates", newPostcode)
          ).map((e) => e.electoraldivisionname);

          // Handle postcode not found
          if (electorates.length !== 0) {
            this.isValidPostcode = true;
            this.electoratesList = electorates;
            this.selectedElectorate = electorates[0];
          }
        } else {
          this.isValidPostcode = false;
          this.selectedElectorate = "";
          this.electoratesList = [];
        }
      } catch (err) {
        console.error(err);
      }
    },
    scrollTop() {
      this.$refs.Selector.$el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    toggleMessage() {
      this.viewMessage = !this.viewMessage;
    },
    logFirstTouch(fieldName) {
      if (!this.formTouched) {
        this.$store.dispatch("logLeafBegin", fieldName);
        this.formTouched = true;
      }
    },
  },
};
</script>

<style scoped>
.form-content {
  padding: var(--lrPadding);
}

.form-content button[type="button"] {
  justify-content: center;
  margin-bottom: 0.5rem;
}

.form-content .privacy-statement p {
  text-align: center;
  margin-bottom: 0 !important;
}

.submit-email-error {
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  color: var(--wwfWhite);
  background-color: var(--errorColor);
  border-radius: 4px;
}

.submit-email-error p {
  line-height: 20px;
}

.back-top {
  display: flex;
  justify-content: center;
  align-items: center;
}

.clickable {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: min-content;
}

.back-top i {
  color: var(--wwfRegenerateAustralia);
}

.back-top p {
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  text-transform: uppercase;
  width: max-content;
  color: var(--wwfRegenerateAustralia);
}

.ra-message-content {
  padding: 10px;
}

.email-btn {
  margin-top: 10px;
}
</style>

<style lang="less">
.ra-message-content .message {
  .loading p,
  .loading i,
  .content p,
  .content ul li {
    color: var(--wwfRegenerateAustralia);
  }
}
</style>


// WEBPACK FOOTER //
// Details.vue?b23cf9c4